import { Component, OnDestroy } from '@angular/core';
import { ProjectScenarioDataGridService } from './project-scenario-list-data-grid.service';
import {
  ProjectsFacade,
  SharedFacade,
  customDecode,
  customEncode,
  encrypt,
} from '@piqet-angular/data';
import { ActivatedRoute, Router } from '@angular/router';
import { ActionButtonComponent } from 'libs/ui/src/lib/action-button/action-button.component';
import { ColDef } from 'ag-grid-community';
import { GridLinkRolesDetailComponent } from 'libs/ui/src/lib/grid-link-roles-detail/grid-link-roles-detail.component';
import { GridMyProjectImageComponent } from 'libs/ui/src/lib/grid-my-project-image/grid-my-project-image.component';
import { ViewportScroller } from '@angular/common';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { AccessPermissionService, CustomModalService } from '@piqet-angular/shared';
import { catchError, of, finalize, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';

@Component({
  selector: 'piqet-angular-project-scenario-list',
  templateUrl: './project-scenario-list.component.html',
  styleUrls: ['./project-scenario-list.component.scss'],
})
export class ProjectScenarioListComponent implements OnDestroy {
  isScreenDisabled: any;
  columnDefs!: ColDef[];
  activatedRoute: any;
  activatedRouteProjectId: any;
  frameworkComponents = {
    actionRenderer: ActionButtonComponent,
    linkRenderer: GridLinkRolesDetailComponent,
    GridMyProjectImageComponent: GridMyProjectImageComponent,
  };
  searchData: any = '';
  sortColumnState: any;
  someSubscription: any;
  gridName: string = 'Project scenario list';
  event: any;
  isHideSideBar = true;
  headerData = {
    materialName: 'Scenario',
  };
  scenarioData: any;
  deleteScenarioItem: Subscription;
  copyScenarioItem: Subscription;
  createAccess: boolean = false;
  showEdit: boolean = false;
  constructor(
    private projectScenarioDataGridService: ProjectScenarioDataGridService,
    public sharedFacade: SharedFacade,
    public projectFacade: ProjectsFacade,
    public route: ActivatedRoute,
    private projectsService: ProjectsService,
    private accessService: AccessPermissionService,
    private sharedService: SharedService,
    private viewportScroller: ViewportScroller,
    private modal: CustomModalService,
    private toaster: ToastrService,
    private router: Router
  ) {
    localStorage.removeItem('scenarioName');
    this.sharedService.scenarioNameFunc('');
    this.activatedRoute = this.route.snapshot.routeConfig?.path;
    const url = location.href.split('/');
    this.activatedRouteProjectId = Number(atob(decodeURIComponent(customDecode(url[6]))));
    this.columnDefs = this.projectScenarioDataGridService.projectScenarioDataColumn(
      this.activatedRoute,
      true
    );
    this.getScenarioList();
    this.deleteScenarioItem = this.projectScenarioDataGridService.deleteScenarioData$.subscribe(
      async (deleteData: any) => {
        this.deleteScenario(deleteData[0]);
      }
    );

    this.copyScenarioItem = this.projectScenarioDataGridService.copyScenarioData$.subscribe(
      async (copyData: any) => {
        this.copyScenario(copyData[0]);
      }
    );
  }

  ngOnInit(): void {
    this.showEdit = this.accessService.hasPermission('edit', this.activatedRoute, 'packaging');
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  triggerScroll(): void {
    this.sharedService.triggerScroll();
  }

  async getScenarioList() {
    this.scenarioData = (
      await this.projectsService.getMyAllProjectScenarioList(this.activatedRouteProjectId, 1)
    ).data;
    const hasMultipleScenarios = this.scenarioData?.scenarioList?.length > 1 ? true : false;
    this.columnDefs = this.projectScenarioDataGridService.projectScenarioDataColumn(
      this.activatedRoute,
      hasMultipleScenarios
    );
    this.projectFacade.storeProjectNameForBreadcrumb(this.scenarioData.projectName);
  }

  async addNewScenario() {
    let response = await this.projectsService.getMyAllProjectScenarioModalDataAsync(
      this.activatedRouteProjectId,
      1,
      0
    );
    this.modal.invoke('newAddProjectScenario', {
      initialState: {
        scenarioData: response.data,
        label: 'Add new custom conversion',
      },
      class: 'modal-xl',
    });
    this.modal.ref?.content.saveScenarioEvent.subscribe((saveScenarioEvent: any) => {
      this.addScenario(saveScenarioEvent);
    });
  }

  async copyScenario(scenarioId: any) {
    let response = await this.projectsService.copyProjectScenario(scenarioId);
    this.modal.invoke('copyProjectScenario', {
      initialState: {
        copyScenarioData: response.data,
        label: 'Copy scenario',
      },
      class: 'modal-xl',
    });
    this.modal.ref?.content.saveCopyProjectScenarioEvent.subscribe(
      (saveCopyProjectScenarioEvent: any) => {
        this.copyScenarioData(saveCopyProjectScenarioEvent);
      }
    );
  }

  editRecord(record: any) {
    this.projectFacade.inlineDataEditScenarioName(record[0], this.activatedRoute);
  }

  addScenario(data: any) {
    this.projectsService
      .addUpdateProjectScenario(data)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.getScenarioList();
          this.sharedService.accessSideBarData(() => {});
        })
      )
      .subscribe(res => {
        const valueToEncrypt = `{ "id": ${res.data.scenarioId}, "packageScenarioId": ${res.data.productPackageScenarioId} }`;
        const encryptedValue = encrypt(valueToEncrypt);
        const encodedValue = encodeURIComponent(this.activatedRouteProjectId);
        if (res.messageType) {
          this.toaster.error(res.message);
        } else {
          this.toaster.success(res.message);
          this.sharedService.scenarioNameFunc(data.name);
          this.router.navigate(
            [
              `projects/projects-setting/${customEncode(
                btoa(encodedValue)
              )}/scenario-configuration`,
            ],
            { queryParams: { param: encryptedValue } }
          );
        }
      });
  }

  deleteScenario(data: any) {
    this.projectsService
      .deleteProjectScenarioData(data.scenarioId)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.getScenarioList();
          this.sharedService.accessSideBarData(() => {});
        })
      )
      .subscribe(res => {
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
  }

  copyScenarioData(data: any) {
    this.projectsService
      .saveCopiedProjectScenario(data)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.getScenarioList();
          this.sharedService.accessSideBarData(() => {});
        })
      )
      .subscribe(res => {
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
  }

  startTour() {
    const valueToEncrypt = `{ "id": ${
      this.scenarioData.scenarioList[0].scenarioId
    }, "haveTour": ${true} }`;
    const encryptedValue = encrypt(valueToEncrypt);
    const encodedValue = encodeURIComponent(this.activatedRouteProjectId);
    this.router.navigate(
      [`projects/projects-setting/${customEncode(btoa(encodedValue))}/scenario-configuration`],
      {
        queryParams: {
          param: encryptedValue,
        },
      }
    );
  }

  changeRowOrder(event: any) {
    if (event) {
      const formattedArray = event.map((item: any, index: number) => ({
        scenarioId: item.scenarioId,
        orderId: index + 1,
      }));
      this.projectsService.addUpdateScenarioOrder(formattedArray).subscribe(res => {
        if (res.messageType) {
          this.toaster.error(res.message);
        } else {
          this.sharedService.accessSideBarData(() => {});
          this.toaster.success(res.message);
        }
      });
    }
  }

  ngOnDestroy() {
    this.deleteScenarioItem.unsubscribe();
    this.copyScenarioItem.unsubscribe();
  }
}
